import React, {useCallback, useEffect, useMemo, useState} from "react"
import SaveIcon from "@mui/icons-material/Save";
import {useSiren} from "../SirenContext";
import {useAuth} from "@/context/user";
import {IconButton, ToggleButton} from "@/features/management-v2/import/inputs";
import {ErrorMessage} from "../ErrorMessage";
import {useCreateTemplate} from "../hooks/useCreateTemplate";
import BasicText, {BasicNumber} from "@/components/form-v2/basic-text";
import BasicSelect from "@/components/form-v2/basic-select";
import {GolemField, GolemText, GolemTextArea} from "@/lib/golem/GolemField";
import {FieldError} from "@/lib/golem/FieldError";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";

import {DepartmentSelectV2} from "@/lib/departments/DepartmentSelectV2";
import {Contact, ContactGroup, DeliveryMethod, Facility, MassWarningTemplate} from "../typedefs";
import {useGetCategories} from "../hooks/useGetCategories";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragHandleIcon from "@mui/icons-material/DragHandle";


import {closestCenter, DndContext, PointerSensor, useSensor, useSensors,} from "@dnd-kit/core";

import {arrayMove, SortableContext, useSortable, verticalListSortingStrategy,} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {DrawingMap} from "@/components/map/DrawingMap";
import {ZoneTimeRangePicker} from "../components/ZoneTimeRangePicker";
import {useToggle} from "@/features/management-v2/import/useToggle";
import {useCharacterLimitMessage} from "../hooks/useCharacterLimitMessage";
import {AudioFileSelect} from "../components/AudioFileSelect";
import {AudioFilePlayer} from "../components/AudioFilePlayer";
import {IntervalFormat, IntervalInput} from "../components/IntervalInput";
import {noop} from "@/lib/misc/noop";
import {usePermissions} from "@/context/modulePermissions";
import clsx from "clsx";
import {PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {fetchContacts} from "@/pages/siren/hooks/useGetContacts";
import {fetchContactGroups} from "@/pages/siren/hooks/useGetContactGroups";
import {fetchFacilities} from "@/pages/siren/hooks/useGetFacilities";
import {isGSM7} from "@/pages/siren/utils/sms";
import {formatPhoneNumber} from "@/lib/pnone/formatPhoneNumber";
import ModalV2 from "@/components/portal/ModalV2";
import {PaginatedResponseV2} from "@/types/PaginatedResponse";
import {platformAPI} from "@/api/axiosConfig";
import {TextToSpeechPreview} from "@/pages/siren/components/TextToSpeechPreview";
import {nilObjectID} from "@/lib/misc/nilObjectID";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {MultiFileUpload} from "@/pages/siren/components/MultiFileUpload";
import {ArrowBack, Delete} from "@mui/icons-material";
import {FileAttachmentRow} from "@/pages/siren/components/FileAttachmentRow";
import {JSONDebug} from "@/pages/siren/components/JSONDebug";
import {RadioSelect} from "@/pages/siren/components/RadioSelect";
import {startCase} from "lodash";
import {fetchValidationErrors, ValidationError} from "../utils/validationErrors";
import {filterMethodsByDriverTypes, useAvailableDriverTypes} from "../hooks/useAvailableDriverTypes";
import DownloadIcon from "@mui/icons-material/Download";
import {generateAndSaveXLSX} from "@/features/management-v2/import/useDownloadXLSX";
import * as XLSX from "xlsx";


type TemplateEditProps = {
    template?: MassWarningTemplate,
    mode: "edit" | "create" | "alert",
    initialContactIDs?: string[],
    onBack?: () => void,
}


function TemplateForm({template, mode, initialContactIDs = [], onBack}: TemplateEditProps) {

    const {clientID} = useSiren()
    const {isDepartmentAdmin} = useAuth()
    const {hasFullAccess} = usePermissions()
    const availableDriverTypes = useAvailableDriverTypes(clientID)
    const readOnly = mode === "edit" && !hasFullAccess("siren_resources")

    const {
        name, setName,
        category, setCategory,
        content, setContent,
        type, setType,
        departmentID, setDepartmentID,
        responseOptions, setResponseOptions,
        smsContent, setSMSContent,
        useSeparateSMSContent, setUseSeparateSMSContent,
        smsResponseMode, setSMSResponseMode,
        zones, setZones,
        zoneTimeRange, setZoneTimeRange,
        speechMode, setSpeechMode,
        audioFileID, setAudioFileID,
        audioFileContainsIVR, setAudioFileContainsIVR,
        contactIDs, setContactIDs,
        contactGroupIDs, setContactGroupIDs,
        facilityIDs, setFacilityIDs,

        useDefaultBroadcastConfig, setUseDefaultBroadcastConfig,
        useDefaultDeliveryMethods, setUseDefaultDeliveryMethods,
        deliveryMethodInterval, setDeliveryMethodInterval,
        deliveryMethodIntervalDisplayFormat, setDeliveryMethodIntervalDisplayFormat,
        broadcastDuration, setBroadcastDuration,
        broadcastDurationDisplayFormat, setBroadcastDurationDisplayFormat,
        cycleCount, setCycleCount,
        cycleInterval, setCycleInterval,
        cycleIntervalDisplayFormat, setCycleIntervalDisplayFormat,
        deliveryMethods, setDeliveryMethods,
        sendAt, setSendAt,
        sendImmediately, setSendImmediately,
        speechModeOptions,
        typeOptions,
        isLoading,
        error,
        create,
        update,
        create_alert,
        validateAlert,
        validatingAlert,
        validationError,
        validationResults,
        validationToken,
        showSaveAsTemplate, setShowSaveAsTemplate,
        templateName, setTemplateName,
        savingTemplate,
        saveAsTemplate,
        validated,
        loading,
        defaultDeliveryMethods,
        defaultBroadcastConfig,
        fileAttachmentIDs, setFileAttachmentIDs,
        stagedUploads, setStagedUploads,
        preferAlphanumericSenderID, setPreferAlphanumericSenderID,
        skipContactsWithValidationErrors, setSkipContactsWithValidationErrors,
        request,
        golemErrors,
    } = useCreateTemplate(template, mode, initialContactIDs);


    const prevClientID = React.useRef<string | null | undefined>(clientID);
    useEffect(() => {
        const prev = prevClientID.current;
        prevClientID.current = clientID;
        if (!prev || prev === clientID) return;
        setContactIDs([]);
        setContactGroupIDs([]);
        setFacilityIDs([]);
        setZones([]);
    }, [clientID]);

    const lockContactSelection = initialContactIDs.length != 0

    const [showZones, toggleShowZones, setShowZones] = useToggle(false);
    const [showContacts, toggleShowContacts, setShowContacts] = useToggle(false);
    const [showContactGroups, toggleShowContactGroups, setShowContactGroups] = useToggle(false);
    const [showFacilities, toggleShowFacilities, setShowFacilities] = useToggle(false);

    const {data} = useGetCategories(clientID);

    const [newResponseOption, setNewResponseOption] = useState("")


    const lockRecipientsNoDept = isDepartmentAdmin && !departmentID

    const title = useMemo(() => {
        if (mode == "alert") {
            return "Send Alert"
        }

        if (mode == "edit") {
            return readOnly ? "View Template" : "Edit Template"
        }
        return "Create Template"
    }, [mode, readOnly])


    const submit = useCallback(() => {
        if (mode == "create") {
            return create()
        }
        if (mode == "edit") {
            return update()
        }

        if (mode == "alert") {
            return create_alert()
        }
    }, [mode, create, update])

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEndResponseOptions = (event: any) => {
        const {active, over} = event;

        if (active.id !== over?.id) {
            const oldIndex = responseOptions.indexOf(active.id);
            const newIndex = responseOptions.indexOf(over.id);
            setResponseOptions(arrayMove(responseOptions, oldIndex, newIndex));
        }
    };

    const handleDragEndDeliveryMethods = (event: any) => {
        const {active, over} = event;

        if (active.id !== over?.id) {
            const oldIndex = deliveryMethods.indexOf(active.id);
            const newIndex = deliveryMethods.indexOf(over.id);
            setDeliveryMethods(arrayMove(deliveryMethods, oldIndex, newIndex));
        }
    };


    const addOption = useCallback(() => {
        if (responseOptions.length >= 9) {
            return
        }

        if (responseOptions.includes(newResponseOption)) {
            return
        }

        if (newResponseOption.trim().length < 2) {
            return
        }

        setResponseOptions([...responseOptions, newResponseOption]);
        setNewResponseOption("")
    }, [newResponseOption])

    const removeOption = useCallback((index: number) => {
        setResponseOptions(responseOptions.filter((item, i) => i != index))
    }, [responseOptions])


    const removeDeliveryMethod = useCallback((method: string) => {
        setDeliveryMethods(prev => prev.filter(item => item !== method));
    }, []);


    const isGSM7SMS = useMemo(() => {
        const toTest = useSeparateSMSContent ? smsContent : content
        return isGSM7(toTest)


    }, [content, smsContent, useSeparateSMSContent])


    const {
        message: contentLimitMessage,
        exceeded: contentExceeded,
    } = useCharacterLimitMessage(content, "{remaining} - Email", 2500)

    const {
        message: contentLimitMessageSMS,
        exceeded: contentExceededSMS,
    } = useCharacterLimitMessage(useSeparateSMSContent ? smsContent : content, "{remaining} - SMS", isGSM7SMS ? 160 : 70)


    const deliveryMethodIntervalChange = useCallback((value: number, formattedValue: number, format: IntervalFormat) => {
        setDeliveryMethodInterval(value)
        setDeliveryMethodIntervalDisplayFormat(format)
    }, [])


    const broadcastDurationChange = useCallback((value: number, formattedValue: number, format: IntervalFormat) => {
        setBroadcastDuration(value)
        setBroadcastDurationDisplayFormat(format)
    }, [])

    const cycleIntervalChange = useCallback((value: number, formattedValue: number, format: IntervalFormat) => {
        setCycleInterval(value)
        setCycleIntervalDisplayFormat(format)
    }, [])


    const handleToggleDefaultBroadcastConfig = useCallback((value: boolean) => {
        if (!value && defaultBroadcastConfig) {
            setBroadcastDuration(defaultBroadcastConfig.broadcastDuration)
            setBroadcastDurationDisplayFormat(defaultBroadcastConfig.broadcastDurationDisplayFormat as IntervalFormat)
            setCycleCount(defaultBroadcastConfig.cycleCount)
            setCycleInterval(defaultBroadcastConfig.cycleInterval)
            setCycleIntervalDisplayFormat(defaultBroadcastConfig.cycleIntervalDisplayFormat as IntervalFormat)
            setDeliveryMethodInterval(defaultBroadcastConfig.deliveryMethodInterval)
            setDeliveryMethodIntervalDisplayFormat(defaultBroadcastConfig.deliveryMethodIntervalDisplayFormat as IntervalFormat)
        }
        setUseDefaultBroadcastConfig(value)
    }, [defaultBroadcastConfig, setUseDefaultBroadcastConfig, setBroadcastDuration, setBroadcastDurationDisplayFormat, setCycleCount, setCycleInterval, setCycleIntervalDisplayFormat, setDeliveryMethodInterval, setDeliveryMethodIntervalDisplayFormat])

    const handleToggleDefaultDeliveryMethods = useCallback((value: boolean) => {
        if (!value && deliveryMethods.length === 0 && defaultDeliveryMethods && defaultDeliveryMethods.length > 0) {
            setDeliveryMethods([...defaultDeliveryMethods])
        }
        setUseDefaultDeliveryMethods(value)
    }, [deliveryMethods, defaultDeliveryMethods, setUseDefaultDeliveryMethods, setDeliveryMethods])

    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<undefined | string>()

    const deliverMethodAddOptions = useMemo(() => {
        const opts = [
            {value: "sms_work_mobile", label: "SMS Work Mobile"},
            {value: "sms_personal_mobile", label: "SMS Personal Mobile"},
            {value: "email_work", label: "Work Email"},
            {value: "email_personal", label: "Personal Email"},
            {value: "call_work_mobile", label: "Call Work Mobile"},
            {value: "call_personal_mobile", label: "Call Personal Mobile"},
            {value: "call_home", label: "Call Home"},
            {value: "app_aurora", label: "Aurora App"},
        ]

        return filterMethodsByDriverTypes(opts, availableDriverTypes)
            .filter(item => !deliveryMethods.includes(item.value))

    }, [deliveryMethods, availableDriverTypes])


    const addDeliveryMethod = useCallback(() => {
        if (!selectedDeliveryMethod) return

        setDeliveryMethods([...deliveryMethods, selectedDeliveryMethod])
        setSelectedDeliveryMethod(undefined)

    }, [deliveryMethods, selectedDeliveryMethod, setSelectedDeliveryMethod])


    const getValidationErrors = useCallback(async (_search: string, page: number) => {
        if (!validationToken) return null
        return await fetchValidationErrors(page, 10, validationToken)
    }, [validationToken])


    const getValidationErrorsColumns = useCallback((e: ValidationError): (string | string[])[] => {
        const contact = e.contact

        return [
            `${contact.firstName} ${contact.lastName}`,
            contact.emailWork,
            e.errors.map(i => validationLabel(i))
        ]
    }, [])


    const getContacts = useCallback(async (search: string, page: number) => {
        return await fetchContacts(page, 10, search, clientID, false, departmentID || null)
    }, [clientID, departmentID])

    const getContactColumns = useCallback((contact: Contact): string[] => {
        return [
            `${contact.firstName} ${contact.lastName}`,
            contact.emailWork,
            contact.emailPersonal,
            formatPhoneNumber(contact.phoneMobileWork?.e164 ?? ""),
            formatPhoneNumber(contact.phoneMobilePersonal?.e164 ?? ""),
            formatPhoneNumber(contact.phoneHome?.e164 ?? ""),
        ]
    }, [])

    const getSelectedContacts = useCallback(async (_search: string, page: number) => {
        const limit = 10
        const start = (page - 1) * limit
        const pageIDs = contactIDs.slice(start, start + limit)
        const results = await Promise.all(pageIDs.map(id => platformAPI.get<Contact>(`api/contacts/${id}`).then(r => r.data)))
        return { results, currentPage: page, totalResults: contactIDs.length, hasMore: contactIDs.length > page * limit }
    }, [contactIDs])

    const getContactsGroups = useCallback(async (search: string, page: number) => {
        return await fetchContactGroups(page, 10, search, clientID, false, undefined, departmentID || null)
    }, [clientID, departmentID])

    const getContactGroupColumns = useCallback((group: ContactGroup): string[] => {
        return [
            `${group.name}`,
            `${group.contactIDs.length}`,
        ]
    }, [])

    const getSelectedContactGroups = useCallback(async (_search: string, page: number) => {
        const limit = 10
        const start = (page - 1) * limit
        const pageIDs = contactGroupIDs.slice(start, start + limit)
        const results = await Promise.all(pageIDs.map(id => platformAPI.get<ContactGroup>(`api/contact-groups/${id}`).then(r => r.data)))
        return { results, currentPage: page, totalResults: contactGroupIDs.length, hasMore: contactGroupIDs.length > page * limit }
    }, [contactGroupIDs])

    const getFacilities = useCallback(async (search: string, page: number) => {
        return await fetchFacilities(page, 10, search, clientID, departmentID || null)
    }, [clientID, departmentID])

    const getFacilityColumns = useCallback((facility: Facility): string[] => {
        return [
            facility.name,
            facility.address?.formatted ?? "",
        ]
    }, [])

    const getSelectedFacilities = useCallback(async (_search: string, page: number) => {
        const limit = 10
        const start = (page - 1) * limit
        const pageIDs = facilityIDs.slice(start, start + limit)
        const results = await Promise.all(pageIDs.map(id => platformAPI.get<Facility>(`api/facilities/${id}`).then(r => r.data)))
        return { results, currentPage: page, totalResults: facilityIDs.length, hasMore: facilityIDs.length > page * limit }
    }, [facilityIDs])

    const showAdvancedFields = useMemo(() => {
        return type == "alert" || mode == "alert"
    }, [type, mode])


    const smsRemaining = useMemo(() => {
        const limit = isGSM7SMS ? 160 : 70
        const c = useSeparateSMSContent ? smsContent : content
        return limit - c.length
    }, [content, smsContent, useSeparateSMSContent])

    const [showRecipientPreview, toggleShowRecipientPreview, setShowRecipientPreview] = useToggle(false)

    const getPreview = useCallback(async (_: string, page: number): Promise<PaginatedResponseV2<Contact>> => {
        const result = await platformAPI.post<PaginatedResponseV2<Contact>>(
            "api/siren/alerts/contact-preview",
            {
                contactIDs,
                contactGroupIDs,
                facilityIDs,
                zones,
                zoneTimeRange,
                clientID,
                page,
                limit: 10,
            },
        )

        return result.data

    }, [contactIDs, contactGroupIDs, facilityIDs, zones, zoneTimeRange, clientID])


    const ivrContent = useMemo(() => {
        return responseOptions.map((text, i) => `Press ${i + 1}, for ${text}.`).join("\n")
    }, [responseOptions])

    const showIVRPreview = useMemo(() => {
        if (responseOptions.length == 0) return false
        if (speechMode == "text_to_speech") return true
        if (speechMode == "audio_file" && !audioFileContainsIVR) return true

        return false
    }, [speechMode, responseOptions, audioFileContainsIVR])


    const applyContentTemplate = useCallback((template: { content: string } | null) => {
        if (!template) return;
        setContent(template.content);
        setSMSContent(template.content);
    }, []);

    const [showValidationTable, toggleShowValidationTable, setShowValidationTable] = useToggle(false)


    const validationLabel = useCallback((key: string) => {
        switch (key) {
            case "sms_country_not_configured":
                return "SMS Country Not Configured"
            case "sms_missing_mobile_phone":
                return "SMS Missing Mobile Phone"
            case "sms_not_allowed":
                return "SMS Not Allowed"
            case "call_country_not_configured":
                return "Phone Call Country Not Configured"
            case "call_missing_mobile_phone":
                return "Phone Call Missing Mobile Phone"
            case "call_missing_home_phone":
                return "Phone Call Missing Home Phone"
            case "call_not_allowed":
                return "Phone Call Not Allowed"
        }

        return startCase(key).replace("Sms", "SMS")
    }, [])


    const [downloadingValidationErrors, setDownloadingValidationErrors] = useState(false)

    const downloadValidationErrors = useCallback(async () => {
        if (!validationToken) return

        setDownloadingValidationErrors(true)
        try {
            const headers = [
                "First Name",
                "Last Name",
                "Email",
                "Personal Email",
                "Work Mobile Phone",
                "Personal Mobile Phone",
                "Home Phone",
                "Errors"
            ]

            const rows: string[][] = []

            let i = 0
            while (true) {
                i++
                const items = await fetchValidationErrors(i, 100, validationToken)
                if (items.results.length == 0) break
                items.results.forEach((v) => {
                    rows.push([
                        v.contact.firstName,
                        v.contact.lastName,
                        v.contact.emailWork,
                        v.contact.emailPersonal,
                        v.contact.phoneMobileWork?.e164 ?? "",
                        v.contact.phoneMobilePersonal?.e164 ?? "",
                        v.contact.phoneHome?.e164 ?? "",
                        v.errors.map(validationLabel).join("\n"),
                    ])
                })
            }

            const timestamp = Math.floor(Date.now() / 1000)
            generateAndSaveXLSX(`validation-errors-${timestamp}.xlsx`, headers, rows)

        } catch (error) {
            console.error(error)
        }

        setDownloadingValidationErrors(false)

    }, [validationToken])

    const selectFromFile = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".csv, .xlsx, .xls";

        input.onchange = async (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (!target.files || target.files.length === 0) return;

            const file = target.files[0];
            const reader = new FileReader();

            reader.onload = (evt) => {
                const data = evt.target?.result;
                if (!data) return;

                let workbook: XLSX.WorkBook;

                if (file.name.endsWith(".csv")) {
                    workbook = XLSX.read(data as string, {type: "string"});
                } else {
                    workbook = XLSX.read(data as ArrayBuffer, {type: "array"});
                }

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: Record<string, any>[] = XLSX.utils.sheet_to_json(worksheet);

                const ids = jsonData
                    .map((row) => {
                        // Find the key that matches "id" ignoring case and spaces
                        const idKey = Object.keys(row).find(
                            (k) => k.trim().toLowerCase() === "id"
                        );
                        if (!idKey) return null;
                        const value = row[idKey];
                        if (typeof value !== "string" && typeof value !== "number") return null;
                        return String(value).trim().toLowerCase();
                    })
                    .filter((id): id is string => id !== null);

                setContactIDs(ids)
            };

            if (file.name.endsWith(".csv")) {
                reader.readAsText(file);
            } else {
                reader.readAsArrayBuffer(file);
            }
        };

        input.click();
    }, []);

    const usingSMS = useMemo(() => {
        const methodsToCheck = ["sms_work_mobile", "sms_personal_mobile"];

        const listToCheck = useDefaultDeliveryMethods && defaultDeliveryMethods
            ? defaultDeliveryMethods
            : deliveryMethods;

        return listToCheck?.some(d => methodsToCheck.includes(d)) ?? false;
    }, [deliveryMethods, defaultDeliveryMethods, useDefaultDeliveryMethods]);


    const disablePreview = useMemo(() => {
        return contactIDs.length == 0 && contactGroupIDs.length == 0 && facilityIDs.length == 0 && zones.length == 0
    }, [contactIDs, contactGroupIDs, facilityIDs, zones])

    if (loading) {
        return (
            <div className="flex items-center justify-center">
                <LoadingSpinner/>
            </div>
        )
    }


    return (
        <>
            <ModalV2 open={showValidationTable} setOpen={setShowValidationTable}>
                <h3 className="text-lg font-bold">Validation Errors</h3>
                <PaginatedSelector
                    disableSelect
                    hideSearch
                    headers={["Name", "Email", "Errors"]}
                    getItems={getValidationErrors}
                    getColumns={getValidationErrorsColumns}
                />

                <IconButton icon={<DownloadIcon/>} text={"Download"} onClick={downloadValidationErrors}
                            loading={downloadingValidationErrors}/>


            </ModalV2>


            <ModalV2 open={showSaveAsTemplate} setOpen={setShowSaveAsTemplate}>
                <h3 className="text-lg font-bold">Save As Template</h3>

                <BasicText label="Name" value={templateName} onChange={setTemplateName}/>
                <IconButton text="Save As Template" disabled={templateName.trim() == ""} loading={savingTemplate}
                            onClick={saveAsTemplate}
                            icon={<SaveIcon/>} color="green"/>
            </ModalV2>


            <ModalV2 open={showZones} setOpen={setShowZones}>
                <fieldset disabled={readOnly} className="border-0 p-0 m-0">
                    <ZoneTimeRangePicker value={zoneTimeRange} onChange={readOnly ? noop : setZoneTimeRange}/>
                </fieldset>
                <DrawingMap onChange={readOnly ? noop : setZones} initialShapes={zones} readOnly={readOnly}/>
                {!readOnly && (
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="px-6 py-2 bg-blue text-white rounded-md shadow-md text-sm font-medium hover:opacity-90 transition-opacity"
                            onClick={() => setShowZones(false)}
                        >
                            Save
                        </button>
                    </div>
                )}
            </ModalV2>

            <ModalV2 open={showContacts} setOpen={setShowContacts}>
                <h3 className="text-lg font-bold">{readOnly ? "Contacts" : "Select Contacts"}</h3>

                <PaginatedSelector
                    headers={["Name", "Email", "Personal Email", "Work Mobile Phone", "Personal Mobile Phone", "Home Phone"]}
                    selectedIDs={contactIDs}
                    setSelectedIDs={readOnly ? undefined : setContactIDs}
                    getItems={readOnly ? getSelectedContacts : getContacts}
                    getColumns={getContactColumns}
                    disableSelect={readOnly}
                    hideSearch={readOnly}
                />
            </ModalV2>
            <ModalV2 open={showContactGroups} setOpen={setShowContactGroups}>
                <h3 className="text-lg font-bold">{readOnly ? "Contact Groups" : "Select Contact Groups"}</h3>
                <PaginatedSelector
                    headers={["Name", "Contact Count"]}
                    selectedIDs={contactGroupIDs}
                    setSelectedIDs={readOnly ? undefined : setContactGroupIDs}
                    getItems={readOnly ? getSelectedContactGroups : getContactsGroups}
                    getColumns={getContactGroupColumns}
                    disableSelect={readOnly}
                    hideSearch={readOnly}
                />
            </ModalV2>

            <ModalV2 open={showFacilities} setOpen={setShowFacilities}>
                <h3 className="text-lg font-bold">{readOnly ? "Facilities" : "Select Facilities"}</h3>
                <PaginatedSelector
                    headers={["Name", "Address"]}
                    columnTemplate="240px 1fr"
                    selectedIDs={facilityIDs}
                    setSelectedIDs={readOnly ? undefined : setFacilityIDs}
                    getItems={readOnly ? getSelectedFacilities : getFacilities}
                    getColumns={getFacilityColumns}
                    disableSelect={readOnly}
                    hideSearch={readOnly}
                />
            </ModalV2>

            <ModalV2 open={showRecipientPreview} setOpen={setShowRecipientPreview}>
                <h3 className="text-lg font-bold">Selected Recipients Preview</h3>
                <PaginatedSelector
                    disableSelect
                    hideSearch
                    headers={["Name", "Email", "Personal Email", "Work Mobile Phone", "Personal Mobile Phone", "Home Phone"]}
                    selectedIDs={contactIDs}
                    getItems={getPreview}
                    getColumns={getContactColumns}
                />


            </ModalV2>

            <fieldset disabled={readOnly} className="flex flex-col gap-4 items-center border-0 p-0 m-0 w-full">
                {/* === DETAILS === */}
                <Section title={title} onBack={onBack}>

                    {mode != "alert" && (
                        <BasicSelect label="Type" value={type} onChange={setType} options={typeOptions}/>
                    )}
                    <GolemText label="Name" value={name} onChange={setName} errors={golemErrors} field="name" required/>

                    {mode != "alert" && (
                        <GolemText label="Category" value={category} onChange={setCategory}
                                   errors={golemErrors} field="category" suggestions={data}/>
                    )}

                    <DepartmentSelectV2 label="Department" initialID={departmentID} clientID={clientID}
                                        error={golemErrors.fieldError("departmentID")}
                                        required={isDepartmentAdmin}
                                        onChange={(d) => {
                                            golemErrors.clearField("departmentID");
                                            setDepartmentID(d?.id ?? "");
                                            setContactIDs([]);
                                            setContactGroupIDs([]);
                                            setFacilityIDs([]);
                                        }}/>
                    <FieldError error={golemErrors.fieldError("departmentID")}/>


                </Section>

                <Section title="Recipients" hide={!showAdvancedFields}>
                    <div className="grid grid-cols-4 gap-4">
                        <RecipientCount label={"Individuals"} number={contactIDs.length}
                                        onClick={toggleShowContacts}
                                        disabled={lockContactSelection || lockRecipientsNoDept}/>
                        <RecipientCount label={"Groups"} number={contactGroupIDs.length}
                                        onClick={toggleShowContactGroups}
                                        disabled={lockContactSelection || lockRecipientsNoDept}/>
                        <RecipientCount label={"Facilities"} number={facilityIDs.length}
                                        onClick={toggleShowFacilities}
                                        disabled={lockContactSelection || lockRecipientsNoDept}/>
                        <RecipientCount label={"Zones"} number={zones.length}
                                        onClick={toggleShowZones} disabled={lockContactSelection}/>
                    </div>
                    {!readOnly && <ButtonV2 label={"Select From File"} onClick={selectFromFile} disabled={lockRecipientsNoDept}/>}
                    <ButtonV2 label={"Preview"} onClick={toggleShowRecipientPreview} disabled={disablePreview}/>
                    <FieldError error={golemErrors.fieldError("alert.recipients")}/>
                </Section>

                {/* === CONTENT */}
                <Section title="Message Content">

                    <GolemTextArea label={"Content"} value={content} onChange={setContent} errors={golemErrors}
                                   field="content" required/>

                    <p className={contentExceeded ? "text-red" : ""}>Characters
                        remaining: {contentLimitMessage} {!useSeparateSMSContent && "|"} <span
                            className={contentExceededSMS ? "text-red" : ""}>{!useSeparateSMSContent && contentLimitMessageSMS}</span>
                    </p>
                    {!useSeparateSMSContent && smsRemaining < 20 && (
                        <div className={"text-red bg-light-red rounded-md px-4 py-2"}>
                            Your message has exceeded the recommended length and will be split into multiple segments
                        </div>
                    )}

                    <ToggleButton text={"Use Custom SMS Message"} onChange={setUseSeparateSMSContent}
                                  value={useSeparateSMSContent}/>

                    {useSeparateSMSContent && (
                        <>
                            <GolemTextArea label={"SMS Content"} value={smsContent} onChange={setSMSContent}
                                           errors={golemErrors} field="smsContent"/>
                            <p className={contentExceededSMS ? "text-red" : ""}>Characters
                                remaining: {contentLimitMessageSMS}</p>

                            {smsRemaining < 20 && (
                                <div className={"text-red bg-light-red rounded-md px-4 py-2"}>
                                    Your message has exceeded the recommended length and will be split into multiple
                                    segments
                                </div>
                            )}
                        </>
                    )}

                    <GolemField errors={golemErrors} field="speechMode">
                        <BasicSelect label="Speech" value={speechMode} onChange={(v) => {
                            golemErrors.clearField("speechMode");
                            setSpeechMode(v);
                        }}
                                     options={speechModeOptions}/>
                    </GolemField>

                    {speechMode === "text_to_speech" && (
                        <TextToSpeechPreview content={content}/>
                    )}

                    {speechMode == "audio_file" && (
                        <>
                            <label className={"mb-1"}>Select Audio File</label>
                            <AudioFileSelect clientID={clientID} value={audioFileID}
                                             onChange={(f) => setAudioFileID(f?.id)}/>


                            {audioFileID && audioFileID != nilObjectID && (
                                <div className={"flex flex-col gap-4"}>
                                    <AudioFilePlayer id={audioFileID}/>
                                    <ToggleButton text={"Audio File Contains Response Options"}
                                                  onChange={setAudioFileContainsIVR}
                                                  value={audioFileContainsIVR}/>
                                </div>
                            )}

                        </>
                    )}

                    <label>File Attachments</label>
                    {fileAttachmentIDs.map((id) => (
                        <FileAttachmentRow key={id} id={id}
                                           onRemove={readOnly ? undefined : () => setFileAttachmentIDs(fileAttachmentIDs.filter(i => i != id))}/>
                    ))}
                    {stagedUploads.map((upload) => (
                        <div key={upload.id} className="grid grid-cols-3 gap-4">
                            <div
                                className="col-span-2 flex items-center w-full bg-white border-light-grey border-1 rounded-md px-4 py-2">
                                {upload.name} <span className="text-sm ml-2 text-light-grey">(staged)</span>
                            </div>
                            {!readOnly && (
                                <div>
                                    <IconButton
                                        text="Remove"
                                        color="orange"
                                        icon={<Delete/>}
                                        onClick={() => setStagedUploads(stagedUploads.filter(u => u.id !== upload.id))}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                    {!readOnly && (
                        <MultiFileUpload clientID={clientID ?? undefined}
                                         onAdd={(upload) => setStagedUploads(prev => [...prev, upload])}/>
                    )}
                    <pre>

                    </pre>
                </Section>

                <Section title="Response Options" hide={!showAdvancedFields}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEndResponseOptions}
                    >
                        <SortableContext items={responseOptions}
                                         strategy={verticalListSortingStrategy}>
                            {responseOptions.map((id, i) => (
                                <ResponseItem key={id} id={id} index={i} remove={removeOption} readOnly={readOnly}/>
                            ))}
                        </SortableContext>
                    </DndContext>
                    {!readOnly && responseOptions.length < 9 && (
                        <div className="grid grid-cols-4 gap-4">
                            <div className="col-span-3">
                                <BasicText value={newResponseOption} onChange={setNewResponseOption}
                                           placeholder={"Add Option"} onEnter={addOption}/>
                            </div>
                            <ButtonV2 label={<AddIcon/>} onClick={addOption}
                                      disabled={newResponseOption.trim().length < 2}/>
                        </div>
                    )}

                    <FieldError error={golemErrors.fieldError("alert.responseOptions")}/>

                    {showIVRPreview && (
                        <TextToSpeechPreview content={ivrContent}/>
                    )}


                    <div className={usingSMS && responseOptions.length > 0 ? "" : "hidden"}>
                        <BasicSelect
                            label="SMS Response"
                            value={smsResponseMode}
                            onChange={(v) => setSMSResponseMode(v)}
                            options={[
                                {label: "SMS Reply", value: "reply", disabled: preferAlphanumericSenderID === true},
                                {label: "Dynamic Link (personalised per recipient)", value: "dynamic_link"},
                                {label: "Static Link", value: "static_link"},
                            ]}/>
                    </div>

                </Section>


                <Section title="Broadcast Configuration" hide={!showAdvancedFields}>
                    <ToggleButton text="Use Default" value={useDefaultBroadcastConfig}
                                  onChange={handleToggleDefaultBroadcastConfig}/>

                    {useDefaultBroadcastConfig && defaultBroadcastConfig && (
                        <div
                            className="flex flex-col gap-2 p-3 bg-light-light-grey rounded-md text-sm text-dark-dark-grey">
                            <div className="flex justify-between"><span
                                className="text-light-grey">Broadcast Duration</span><span>{formatInterval(defaultBroadcastConfig.broadcastDuration, defaultBroadcastConfig.broadcastDurationDisplayFormat)}</span>
                            </div>
                            <div className="flex justify-between"><span
                                className="text-light-grey">Cycle Count</span><span>{defaultBroadcastConfig.cycleCount}</span>
                            </div>
                            <div className="flex justify-between"><span
                                className="text-light-grey">Cycle Interval</span><span>{formatInterval(defaultBroadcastConfig.cycleInterval, defaultBroadcastConfig.cycleIntervalDisplayFormat)}</span>
                            </div>
                            <div className="flex justify-between"><span className="text-light-grey">Delivery Method Interval</span><span>{formatInterval(defaultBroadcastConfig.deliveryMethodInterval, defaultBroadcastConfig.deliveryMethodIntervalDisplayFormat)}</span>
                            </div>
                        </div>
                    )}
                    {useDefaultBroadcastConfig && !defaultBroadcastConfig && (
                        <p className="text-sm text-light-grey">No default broadcast config configured</p>
                    )}
                    {useDefaultBroadcastConfig && (
                        <p className="text-xs text-light-grey">If the default config changes before the alert starts,
                            the updated values will be used.</p>
                    )}

                    {!useDefaultBroadcastConfig && (
                        <>
                            <IntervalInput
                                label="Broadcast Duration"
                                initialValue={broadcastDuration}
                                initialFormat={broadcastDurationDisplayFormat}
                                onChange={broadcastDurationChange}
                            />
                            <FieldError error={golemErrors.fieldError("alert.broadcastConfig.broadcastDuration")}/>

                            <GolemField errors={golemErrors} field="alert.broadcastConfig.cycleCount">
                                <BasicNumber label="Cycle Count" type={"number"} value={cycleCount}
                                             onChange={(v) => {
                                                 golemErrors.clearField("alert.broadcastConfig.cycleCount");
                                                 setCycleCount(v);
                                             }}
                                             align="left"
                                             min={1} max={3}/>
                            </GolemField>

                            <IntervalInput
                                label="Cycle Interval"
                                initialValue={cycleInterval}
                                initialFormat={cycleIntervalDisplayFormat}
                                onChange={cycleIntervalChange}
                            />
                            <FieldError error={golemErrors.fieldError("alert.broadcastConfig.cycleInterval")}/>

                            <IntervalInput
                                label="Delivery Method Interval"
                                initialValue={deliveryMethodInterval}
                                initialFormat={deliveryMethodIntervalDisplayFormat}
                                onChange={deliveryMethodIntervalChange}
                            />
                            <FieldError error={golemErrors.fieldError("alert.broadcastConfig.deliveryMethodInterval")}/>
                        </>
                    )}

                </Section>

                <Section title="Delivery Methods" hide={!showAdvancedFields}>

                    <JSONDebug data={deliveryMethods} label="Delivery Methods"/>
                    <JSONDebug data={availableDriverTypes} label="Drivers"/>

                    <ToggleButton text="Use Default" value={useDefaultDeliveryMethods}
                                  onChange={handleToggleDefaultDeliveryMethods}/>

                    {useDefaultDeliveryMethods ? (
                        <>
                            <div
                                className="flex flex-col gap-2 p-3 bg-light-light-grey rounded-md text-sm text-dark-dark-grey">
                                {defaultDeliveryMethods && defaultDeliveryMethods.length > 0
                                    ? defaultDeliveryMethods.map((m, i) => (
                                        <div key={m} className="flex justify-between">
                                            <span className="text-light-grey">{i + 1}</span>
                                            <span>{formatDeliveryMethod(m)}</span>
                                        </div>
                                    ))
                                    : <p className="text-light-grey">No default delivery methods configured</p>
                                }
                            </div>
                            <p className="text-xs text-light-grey">If the default delivery methods change before the
                                alert starts, the updated values will be used.</p>
                        </>
                    ) : (
                        <>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEndDeliveryMethods}
                            >
                                <SortableContext items={deliveryMethods}
                                                 strategy={verticalListSortingStrategy}>
                                    {deliveryMethods.map((method, i) => (
                                        <DeliveryMethodItem key={method} deliveryMethod={method}
                                                            index={i}
                                                            remove={() => removeDeliveryMethod(method)}
                                                            disabled={readOnly}/>
                                    ))}
                                </SortableContext>
                            </DndContext>
                            {!readOnly && (
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="col-span-3">
                                        <BasicSelect disabled={deliverMethodAddOptions.length == 0}
                                                     options={deliverMethodAddOptions}
                                                     value={selectedDeliveryMethod}
                                                     onChange={setSelectedDeliveryMethod} useEmptyOption={true}/>
                                    </div>
                                    <ButtonV2 label={<AddIcon/>} onClick={addDeliveryMethod}
                                              disabled={deliverMethodAddOptions.length == 0 || !selectedDeliveryMethod}/>
                                </div>
                            )}
                            <FieldError error={golemErrors.fieldError("alert.deliveryMethods")}/>
                        </>
                    )}

                    <GolemField errors={golemErrors} field="smsSenderID">
                        <BasicSelect
                            label="Preferred SMS Sender ID"
                            disabled={!usingSMS}
                            value={preferAlphanumericSenderID === undefined ? "" : preferAlphanumericSenderID === true ? "alphanumeric" : "long_code"}
                            onChange={(v) => {
                                golemErrors.clearField("smsSenderID");
                                if (v === "") setPreferAlphanumericSenderID(undefined);
                                else if (v === "alphanumeric") {
                                    setPreferAlphanumericSenderID(true);
                                    if (smsResponseMode === "reply") setSMSResponseMode("dynamic_link");
                                } else setPreferAlphanumericSenderID(false);
                            }}
                            options={[
                                {label: "No preference", value: ""},
                                {label: "Long Code", value: "long_code"},
                                {label: "Alphanumeric", value: "alphanumeric", disabled: smsResponseMode === "reply"},
                            ]}/>
                    </GolemField>
                </Section>

                <Section title="Scheduling" hide={mode != "alert"}>
                    <ToggleButton text="Send Immediately" value={sendImmediately} onChange={setSendImmediately}/>

                    {!sendImmediately && (
                        <BasicText value={sendAt} onChange={setSendAt} label={"Start Siren Alert At"}
                                   type="datetime-local"/>
                    )}
                </Section>

                <Section title="Validate" hide={mode != "alert"}>
                    <RadioSelect
                        value={skipContactsWithValidationErrors}
                        onChange={setSkipContactsWithValidationErrors}
                        options={[{
                            label: "Skip Contacts With Validation Errors",
                            value: true,
                        }]}
                    />
                    {validationResults && Object.keys(validationResults).length != 0 && (
                        <>
                            <div className="grid grid-cols-2" data-field-error>{
                                Object.keys(validationResults).map((key) => (
                                    <>
                                        <div className="font-bold" key={key}>{validationLabel(key)}</div>
                                        <div key={`${key}_num`}>{validationResults[key]}</div>
                                    </>
                                ))}
                            </div>
                            <ButtonV2 label="Show Errors" onClick={toggleShowValidationTable}/>

                        </>

                    )}


                    <ErrorMessage text={validationError}/>
                    {validationResults != undefined && Object.keys(validationResults).length === 0 && (
                        <p className="text-center">No validation errors</p>
                    )}


                    <ButtonV2 label={"Validate"} onClick={validateAlert} loading={validatingAlert}/>

                </Section>

                <div className={"flex flex-col gap-4 w-full md:max-w-3xl"}>
                    <JSONDebug data={request} label="Request Body"/>

                    {mode == "alert" && (
                        <IconButton text={"Save As Template"} onClick={() => setShowSaveAsTemplate(true)}
                                    icon={<SaveIcon/>} color="blue"/>
                    )}

                    {mode == "alert" && (
                        <IconButton text={"Create Alert"} loading={isLoading} onClick={submit}
                                    disabled={!validated || isLoading}
                                    icon={<SaveIcon/>} color="green"/>
                    )}

                    {mode != "alert" && !readOnly && (
                        <IconButton text={"Save"} loading={isLoading} onClick={submit}
                                    disabled={isLoading}
                                    icon={<SaveIcon/>} color="green"/>

                    )}


                    {(mode == "alert" && !validated) && (
                        <p className="text-center">Please validate alert</p>
                    )}
                    <ErrorMessage text={error}/>

                </div>


            </fieldset>

        </>

    )
}

export default TemplateForm

function ResponseItem({id, index, remove, readOnly = false}: { id: string, index: number, remove: (index: number) => void, readOnly?: boolean }) {
    const {attributes, listeners, setNodeRef, transform, transition} =
        useSortable({id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleRemove = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        remove(index)
    }, [index])

    return (
        <div ref={setNodeRef} style={style}
             className="w-full bg-white border-light-grey border-1 rounded-md px-4 py-2 flex flex-row">
            <div className="flex flex-row gap-4 flex-grow cursor-move"  {...attributes} {...listeners}>
                <div>
                    <DragHandleIcon/>
                </div>
                <div>
                    {index + 1}
                </div>
                <div className="flex-grow">
                    {id}
                </div>
            </div>

            {!readOnly && (
                <button onClick={handleRemove}>
                    <CloseIcon/>
                </button>
            )}
        </div>
    );
}

function formatDeliveryMethod(method: string): string {
    return method
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\bSms\b/g, "SMS")
}

function formatInterval(seconds: number, format: string): string {
    switch (format) {
        case "hour":
            return `${seconds / 3600} hr`;
        case "minute":
            return `${seconds / 60} min`;
        default:
            return `${seconds} sec`;
    }
}

export function DeliveryMethodItem({index, deliveryMethod, remove, disabled = false}: {
    index: number,
    deliveryMethod: DeliveryMethod,
    remove: () => void
    disabled?: boolean
}) {
    const {attributes, listeners, setNodeRef, transform, transition} =
        useSortable({id: deliveryMethod});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleRemove = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return
        e.stopPropagation();
        e.preventDefault();
        remove()
    }, [disabled, remove])


    const name = useMemo(() => {
        switch (deliveryMethod) {
            case "sms_work_mobile":
                return "SMS Work Mobile";
            case "sms_personal_mobile":
                return "SMS Personal Mobile";
            case "email_work":
                return "Work Email";
            case "email_personal":
                return "Personal Email";
            case "call_work_mobile":
                return "Call Work Mobile";
            case "call_personal_mobile":
                return "Call Personal Mobile";
            case "call_home":
                return "Call Home";
            case "app_aurora":
                return "Aurora App";
        }

        return deliveryMethod;
    }, [deliveryMethod]);

    return (
        <div ref={setNodeRef} style={style}
             className="w-full bg-white border-light-grey border-1 rounded-md px-4 py-2 flex flex-row">
            <div className="flex flex-row gap-4 flex-grow cursor-move"  {...attributes} {...listeners}>
                {!disabled && (<div>
                    <DragHandleIcon/>
                </div>)}
                <div>
                    {index + 1}
                </div>
                <div className="flex-grow">
                    {name}
                </div>
            </div>

            {!disabled && (<button onClick={handleRemove}>
                <CloseIcon/>
            </button>)}
        </div>
    );
}

type RecipientCountProps = {
    label: string,
    number: number,
    onClick?: () => void,
    disabled?: boolean
}

function RecipientCount(
    {
        label,
        number,
        onClick = noop,
        disabled
    }: RecipientCountProps
) {

    const className = useMemo(() => {
        return clsx(
            "flex flex-col  border-light-grey border-1 rounded-md p-2 gap-2",
            {
                "cursor-pointer": !disabled,
                "opacity-50": disabled,
                "opacity-70": !disabled && number == 0
            }
        )
    }, [number, disabled]);

    const handleClick = useCallback(() => {
        if (disabled) {
            return
        }
        onClick()
    }, [disabled, onClick]);


    return (
        <div className="flex flex-col gap-4">
            <div className={className} onClick={handleClick}>
                <div className="text-center font-bold">{label}</div>
                <div className="text-center">{number}</div>
            </div>
            <button onClick={handleClick} disabled={disabled}
                    className="bg-blue rounded-md w-full text-white flex items-center justify-center py-2 shadow-md hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed">
                <AddIcon/>
            </button>
        </div>
    )
}


function Section({children, title, hide = false, onBack}: {
    children: React.ReactNode,
    title: string,
    hide?: boolean,
    onBack?: () => void
}) {
    if (hide) {
        return null
    }

    return (
        <div className={"bg-white shadow-md p-4 flex flex-col gap-4 w-full md:max-w-3xl"}>
            <div className="flex items-center gap-2">
                {onBack && (
                    <button onClick={onBack} className="text-grey hover:text-dark-dark-grey transition-colors">
                        <ArrowBack style={{fontSize: 22}}/>
                    </button>
                )}
                <h3 className="text-lg font-bold">{title}</h3>
            </div>

            {children}
        </div>
    )

}

